import React, { useState, useCallback, useEffect } from "react";
import {
  Stack,
  Box,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  FormHelperText,
} from "@mui/material";
import { IconExpandMore, IconExpandLess, IconCancel } from "@/icons";
import { RoleChip } from "./RoleChip";
import { generateDebug } from "@/utils";
const debug = generateDebug('BatchUserRow');
export type UserRole = "student" | "instructor" | "instructor-team";

export interface UserRowData {
  id: string;
  email: string;
  roles: UserRole[];
  firstName?: string;
  lastName?: string;
  message?: string;
  error?: string;
}

interface BatchUserRowProps {
  data: UserRowData;
  availableRoles: UserRole[];
  onChange: (id: string, updates: Partial<UserRowData>) => void;
  onDelete?: (id: string) => void; // Make delete optional since it's handled by parent
}

export function BatchUserRow({
  data,
  availableRoles,
  onChange,
}: BatchUserRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasOptionalData = !!(data.firstName || data.lastName || data.message);

  // Auto-expand if there's data in optional fields
  useEffect(() => {
    if (hasOptionalData && !expanded) {
      setExpanded(true);
    }
  }, [hasOptionalData]);

  const handleExpandClick = useCallback(() => {
    if (!hasOptionalData || !expanded) {
      setExpanded(!expanded);
    }
  }, [expanded, hasOptionalData]);

  const handleRoleChange = useCallback(
    (event: any) => {
      onChange(data.id, { roles: event.target.value });
    },
    [data.id, onChange]
  );

  return (
    <Stack
      spacing={2}
      sx={{
        position: "relative",
        border: "1px solid",
        borderColor: data.error || !data.roles.length ? "error.main" : "divider",
        borderRadius: 1,
        p: 2,
        flexGrow: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          placeholder="Email"
          label="Email"
          value={data.email}
          onChange={(e) => onChange(data.id, { email: e.target.value })}
          error={!!data.error}
          helperText={data.error}
          sx={{ flex: 1 }}
        />
        <FormControl sx={{ minWidth: 300 }} size="small">
          <InputLabel id={`role-select-${data.id}-label`}>Roles</InputLabel>
          <Select
            labelId={`role-select-${data.id}-label`}
            multiple
            label="Roles"
            value={data.roles}
            onChange={handleRoleChange}
            error={!data.roles.length}
            renderValue={(selected) => (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexWrap: "wrap", gap: 0.5 }}
              >
                {(selected as UserRole[]).map((role) => (
                  <RoleChip key={role} roleId={role} onDelete={() => {
                    const newRoles = data.roles.filter(r => r !== role);
                    handleRoleChange({ target: { value: newRoles } });
                  }} 
                  deleteIcon={
                    <IconCancel
                      onMouseDown={(event) => event.stopPropagation()}
                    />
                  }/>
                ))}
              </Stack>
            )}
          >
            {availableRoles.filter(role => !data.roles.includes(role)).map((role) => (
              <MenuItem key={role} value={role}>
                <RoleChip roleId={role} />
              </MenuItem>
            ))}
          </Select>
          {!data.roles.length && <FormHelperText error>
            Assign Roles
          </FormHelperText>}
          {data.error && <FormHelperText error>
            {data.error}
          </FormHelperText>}
        </FormControl>
        <IconButton
          size="small"
          onClick={handleExpandClick}
          disabled={expanded && hasOptionalData}
          sx={{
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
            color: expanded && hasOptionalData ? "action.disabled" : "default",
          }}
        >
          {expanded ? <IconExpandLess /> : <IconExpandMore />}
        </IconButton>
      </Stack>
      <Collapse in={expanded}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="First Name"
            fullWidth
            placeholder="First Name"
            value={data.firstName || ""}
            onChange={(e) => onChange(data.id, { firstName: e.target.value })}
          />
          <TextField
            size="small"
            placeholder="Last Name"
            fullWidth
            label="Last Name"
            value={data.lastName || ""}
            onChange={(e) => onChange(data.id, { lastName: e.target.value })}
          />
        </Stack>
          <TextField
            size="small"
            placeholder="Message"
            label="Message"
            value={data.message || ""}
            onChange={(e) => onChange(data.id, { message: e.target.value })}
            multiline
            minRows={1}
            maxRows={3}
            sx={{ flex: 1 }}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
}
